// Account › Notifications (`/c/{slug}/notifications`, from the account menu; SEARCH-NOTIF, 06
// §Notification preferences; reworked by INBOX-PREFS, founder-approved 2026-10-29, ClickUp-style).
// The caller's own preferences in this company, saved as they change (`notifications.setPrefs`):
// - Preset: Everything / Only what needs me / Manager / Quiet, or the company's default; "Custom"
//   once a row differs. Choosing one clears the row edits;
// - Pause: alerts and the digest hold for an hour, today, or until Monday; Resume;
// - The grid: each notification type × Inbox / Alert / Email / Digest. Inbox off greys the rest;
//   Waiting on you keeps its inbox (locked) and, with alerts off, its digest; Email shows disabled
//   while no email channel exists;
// - Per-item overrides (a task or a DE: All / Mentions / Mute), listed when there are any;
// - Company default (managers): the preset people start from and the nudge hours
//   (`companies.update` settings.notifications);
// - Digest frequency, the Friday report, quiet hours, this browser's desktop permission and Web
//   Push, and every joined channel's level and mute.
// Explanations sit behind (i) popovers. The rules themselves are the server's
// (domain-work notification-prefs.ts).
import {
  CATEGORY_HINTS,
  CATEGORY_LABELS,
  DESTINATION_LABELS,
  NOTIFY_CATEGORIES,
  NOTIFY_DESTINATIONS,
  NOTIFY_PRESETS,
  type NotifyCategory,
  type NotifyDestination,
  type NotifyPreset,
  PRESET_LABELS,
} from "@companyos/domain-work/chat";
import {
  Button,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SettingsGroup,
  SettingsRow,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellIcon, PauseIcon, PlayIcon, XIcon } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import type { Api } from "../../api/client";
import { useDeNames } from "../agents/runs-screen";
import { BriefSettingsRow } from "../briefs/brief-settings-modal";
import { channelsQuery, chatKeys } from "../chat/chat-queries";
import { type CompanyAccess, isManager } from "../company";
import { formatDateTime } from "../format";
import { companyQuery, keys } from "../queries";
import { ReportEmailGroup } from "../reports/report-email";
import { LoadError, Loading } from "../screen-parts";
import { SetupPage } from "../settings/setup-page";
import { useFeature } from "../use-feature";
import { notificationKeys, notificationPrefsQuery } from "./notification-queries";
import { PushToggle } from "./push-toggle";

type Prefs = Awaited<ReturnType<Api["client"]["notifications"]["prefs"]>>;
type Put = Parameters<Api["client"]["notifications"]["setPrefs"]>[0]["body"];

export { notificationKeys, notificationPrefsQuery };

const FREQUENCIES = [
  { value: "off", label: "Off" },
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
] as const;

const LEVELS = [
  { value: "all", label: "Every message" },
  { value: "mentions", label: "Mentions only" },
  { value: "none", label: "Nothing" },
] as const;

const OVERRIDE_LEVELS = [
  { value: "all", label: "All" },
  { value: "mentions", label: "Mentions" },
  { value: "mute", label: "Mute" },
] as const;

const PAUSES = [
  { value: "1h", label: "1 hour" },
  { value: "today", label: "Today" },
  { value: "monday", label: "Until Monday" },
] as const;

/** This browser's time zone, and the zones it knows (for the quiet hours' picker). */
const LOCAL_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const ZONES: string[] = (() => {
  try {
    const list = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.(
      "timeZone",
    );
    return list?.length ? [...new Set(["UTC", ...list])] : ["UTC", LOCAL_ZONE];
  } catch {
    return ["UTC", LOCAL_ZONE];
  }
})();

type Permission = "granted" | "denied" | "default" | "unsupported";
function permissionNow(): Permission {
  return typeof window !== "undefined" && "Notification" in window
    ? (window.Notification.permission as Permission)
    : "unsupported";
}

/** A short label with its (i) beside it. */
function Labelled({
  children,
  tip,
  about,
}: {
  children: ReactNode;
  tip: ReactNode;
  about: string;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <InfoTip label={`About ${about}`}>{tip}</InfoTip>
    </span>
  );
}

export function NotificationSettings({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const queryClient = useQueryClient();
  const prefs = useQuery(notificationPrefsQuery(api, co));
  const save = useMutation({
    mutationFn: (body: Put) => api.client.notifications.setPrefs({ params: { co }, body }),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationKeys.prefs(co), data);
      toast.add({ title: "Saved" });
    },
    onError: (e) =>
      toast.add({ title: "Not saved", description: e instanceof Error ? e.message : String(e) }),
  });
  const onChange = (body: Put) => save.mutate(body);

  return (
    <SetupPage
      crumbs={["Account"]}
      icon={BellIcon}
      title="Notifications"
      slot="notification-settings"
    >
      {prefs.isPending ? (
        <Loading label="Loading your notification settings" rows={6} />
      ) : prefs.isError ? (
        <LoadError what="Your notification settings" error={prefs.error} />
      ) : (
        <>
          <PresetGroup prefs={prefs.data} onChange={onChange} />
          <RoutesGroup prefs={prefs.data} onChange={onChange} />
          {prefs.data.overrides.length ? (
            <OverridesGroup api={api} co={co} prefs={prefs.data} onChange={onChange} />
          ) : null}
          {isManager(company) ? <CompanyDefaultGroup api={api} co={co} /> : null}
          <DigestGroup prefs={prefs.data} onChange={onChange} />
          {/* REPORTS-UI: the weekly Friday report email (owners, admins, client viewers). */}
          <ReportEmailGroup api={api} company={company} />
          {/* DE-BRIEFS: the rhythm of the briefs the caller's DEs send them (`agents` add-on). */}
          <BriefsGroup api={api} company={company} />
          {/* DE-REPLY-STYLE: how long DEs' chat replies are (`agents` add-on). */}
          <ReplyStyleGroup api={api} company={company} />
          <QuietHoursGroup prefs={prefs.data} onChange={onChange} />
          <DesktopGroup api={api} co={co} />
          <ChannelsGroup api={api} company={company} />
        </>
      )}
    </SetupPage>
  );
}

/** The preset picker's value: a preset, the company's default, or "custom" (rows edited). */
function presetValue(p: Prefs): string {
  if (p.customized) return "custom";
  return p.preset ?? "company";
}

function PresetGroup({ prefs, onChange }: { prefs: Prefs; onChange: (b: Put) => void }) {
  const paused = prefs.paused_until && new Date(prefs.paused_until).getTime() > Date.now();
  return (
    <SettingsGroup data-slot="notification-preset">
      <SettingsRow
        label={
          <Labelled
            about="presets"
            tip="A starting point for the grid below. Picking one clears your own changes."
          >
            Preset
          </Labelled>
        }
      >
        <NativeSelect
          aria-label="Preset"
          value={presetValue(prefs)}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "custom") return;
            onChange({ preset: v === "company" ? null : (v as NotifyPreset) });
          }}
        >
          {prefs.customized ? (
            <NativeSelectOption value="custom" disabled>
              Custom
            </NativeSelectOption>
          ) : null}
          <NativeSelectOption value="company">
            Company default ({PRESET_LABELS[prefs.company_preset]})
          </NativeSelectOption>
          {NOTIFY_PRESETS.map((p) => (
            <NativeSelectOption key={p} value={p}>
              {PRESET_LABELS[p]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </SettingsRow>
      <SettingsRow
        data-slot="notification-pause"
        label={
          <Labelled about="pausing" tip="Alerts and the digest wait. The inbox still fills.">
            {paused && prefs.paused_until
              ? `Paused until ${formatDateTime(prefs.paused_until)}`
              : "Pause"}
          </Labelled>
        }
      >
        {paused ? (
          <Button size="sm" variant="outline" onClick={() => onChange({ pause: null })}>
            <PlayIcon data-icon="inline-start" />
            Resume
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {PAUSES.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant="outline"
                aria-label={`Pause ${p.label.toLowerCase()}`}
                onClick={() => onChange({ pause: { for: p.value, time_zone: LOCAL_ZONE } })}
              >
                {p.value === "1h" ? <PauseIcon data-icon="inline-start" /> : null}
                {p.label}
              </Button>
            ))}
          </div>
        )}
      </SettingsRow>
    </SettingsGroup>
  );
}

function RoutesGroup({ prefs, onChange }: { prefs: Prefs; onChange: (b: Put) => void }) {
  return (
    <SettingsGroup title="What reaches you">
      <table
        aria-label="Notification types by destination"
        className="w-full table-fixed border-collapse text-[13px]"
        data-slot="notification-grid"
      >
        <colgroup>
          <col />
          {NOTIFY_DESTINATIONS.map((d) => (
            <col key={d} className="w-[4.75rem] max-md:w-14" />
          ))}
        </colgroup>
        <thead>
          <tr className="border-b border-subtle text-2xs font-semibold tracking-caps text-muted-foreground uppercase">
            <th scope="col" className="px-4 py-2 text-left font-semibold">
              Type
            </th>
            {NOTIFY_DESTINATIONS.map((d) => (
              <th key={d} scope="col" className="px-1 py-2 text-center font-semibold">
                {d === "email" && !prefs.email_available ? (
                  <span className="inline-flex items-center justify-center gap-0.5">
                    {DESTINATION_LABELS[d]}
                    <InfoTip label="About Email">No email channel yet.</InfoTip>
                  </span>
                ) : (
                  DESTINATION_LABELS[d]
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {NOTIFY_CATEGORIES.map((c) => (
            <RouteRow key={c} c={c} prefs={prefs} onChange={onChange} />
          ))}
        </tbody>
      </table>
    </SettingsGroup>
  );
}

function RouteRow({
  c,
  prefs,
  onChange,
}: {
  c: NotifyCategory;
  prefs: Prefs;
  onChange: (b: Put) => void;
}) {
  const route = prefs.routes[c];
  const label = CATEGORY_LABELS[c];
  const locked = (d: NotifyDestination) =>
    c === "waiting" && (d === "inbox" || (d === "digest" && !route.alert));
  const disabled = (d: NotifyDestination) =>
    locked(d) || (d === "email" && !prefs.email_available) || (d !== "inbox" && !route.inbox);
  return (
    <tr data-category={c} className="border-t border-subtle first-of-type:border-t-0">
      <th scope="row" className="min-w-0 px-4 py-2 text-left font-normal">
        <span className="inline-flex min-w-0 items-center gap-1 font-medium text-foreground">
          <span className="truncate">{label}</span>
          <InfoTip label={`About ${label}`}>{CATEGORY_HINTS[c]}</InfoTip>
        </span>
      </th>
      {NOTIFY_DESTINATIONS.map((d) => (
        <td key={d} className="px-1 py-2 text-center">
          <span className="inline-flex items-center justify-center gap-0.5">
            <Switch
              size="sm"
              aria-label={`${label}: ${DESTINATION_LABELS[d]}`}
              checked={route[d]}
              disabled={disabled(d)}
              onCheckedChange={(v) => onChange({ routes: { [c]: { [d]: Boolean(v) } } })}
            />
            {locked(d) && d === "inbox" ? (
              <InfoTip label="About the locked inbox" className="max-md:hidden">
                Waiting items always reach your inbox, so a DE never waits unseen.
              </InfoTip>
            ) : null}
          </span>
        </td>
      ))}
    </tr>
  );
}

function OverridesGroup({
  api,
  co,
  prefs,
  onChange,
}: {
  api: Api;
  co: string;
  prefs: Prefs;
  onChange: (b: Put) => void;
}) {
  const des = useDeNames(api, co);
  return (
    <SettingsGroup
      title="Per item"
      count={prefs.overrides.length}
      data-slot="notification-overrides"
    >
      {prefs.overrides.map((o) => {
        const name =
          o.subject_name ??
          (o.subject_kind === "agent"
            ? (des.get(o.subject_id) ?? "A DE no longer here")
            : "A task you can no longer open");
        return (
          <SettingsRow key={`${o.subject_kind}:${o.subject_id}`} label={name}>
            <NativeSelect
              size="sm"
              aria-label={`${name}: notify`}
              value={o.level}
              onChange={(e) =>
                onChange({
                  overrides: [
                    {
                      subject_kind: o.subject_kind,
                      subject_id: o.subject_id,
                      level: e.target.value as (typeof OVERRIDE_LEVELS)[number]["value"],
                    },
                  ],
                })
              }
            >
              {OVERRIDE_LEVELS.map((l) => (
                <NativeSelectOption key={l.value} value={l.value}>
                  {l.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={`Remove ${name}`}
              onClick={() =>
                onChange({
                  overrides: [
                    { subject_kind: o.subject_kind, subject_id: o.subject_id, level: null },
                  ],
                })
              }
            >
              <XIcon aria-hidden="true" />
            </Button>
          </SettingsRow>
        );
      })}
    </SettingsGroup>
  );
}

/** Managers: the preset people start from, and how long a waiting item waits for its reminder. */
function CompanyDefaultGroup({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const q = useQuery(companyQuery(api, co));
  const save = useMutation({
    mutationFn: async (next: { default_preset?: NotifyPreset; nudge_hours?: number }) => {
      // Merge into the company's settings as they are now, not as this page last read them (a
      // setting saved elsewhere since is kept).
      const fresh = await qc.fetchQuery({ ...companyQuery(api, co), staleTime: 0 });
      const settings = fresh.settings ?? {};
      return api.client.companies.update({
        params: { co },
        body: { settings: { ...settings, notifications: { ...settings.notifications, ...next } } },
      });
    },
    onSuccess: (detail) => {
      qc.setQueryData(keys.company(co), detail);
      void qc.invalidateQueries({ queryKey: notificationKeys.prefs(co) });
      toast.add({ title: "Saved" });
    },
    onError: (e) =>
      toast.add({ title: "Not saved", description: e instanceof Error ? e.message : String(e) }),
  });
  const n = q.data?.settings.notifications;
  const nudge = n?.nudge_hours ?? 4;
  const [draft, setDraft] = useState(String(nudge));
  useEffect(() => setDraft(String(nudge)), [nudge]);
  if (q.isPending) return <Loading label="Loading the company default" rows={1} />;
  if (q.isError) return null;
  return (
    <SettingsGroup title="Company default" data-slot="notification-company-default">
      <SettingsRow
        label={
          <Labelled about="the company default" tip="The preset people start from.">
            Preset
          </Labelled>
        }
      >
        <NativeSelect
          aria-label="Company default preset"
          value={n?.default_preset ?? "manager"}
          onChange={(e) => save.mutate({ default_preset: e.target.value as NotifyPreset })}
        >
          {NOTIFY_PRESETS.map((p) => (
            <NativeSelectOption key={p} value={p}>
              {PRESET_LABELS[p]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </SettingsRow>
      <SettingsRow
        label={
          <Labelled
            about="nudges"
            tip="How long a waiting item waits before one reminder, and as long again before it is handed on."
          >
            Nudge after (hours)
          </Labelled>
        }
      >
        <Input
          aria-label="Nudge after (hours)"
          type="number"
          min={1}
          max={168}
          className="w-20"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const h = Math.round(Number(draft));
            if (Number.isFinite(h) && h >= 1 && h <= 168 && h !== nudge)
              save.mutate({ nudge_hours: h });
            else setDraft(String(nudge));
          }}
        />
      </SettingsRow>
    </SettingsGroup>
  );
}

function DigestGroup({ prefs, onChange }: { prefs: Prefs; onChange: (b: Put) => void }) {
  return (
    <SettingsGroup title="Email digest">
      <SettingsRow
        label="How often"
        hint={
          prefs.last_digest_at ? `Last sent ${formatDateTime(prefs.last_digest_at)}` : undefined
        }
        htmlFor="digest-frequency"
      >
        <NativeSelect
          id="digest-frequency"
          value={prefs.digest_frequency}
          onChange={(e) =>
            onChange({ digest_frequency: e.target.value as Prefs["digest_frequency"] })
          }
        >
          {FREQUENCIES.map((f) => (
            <NativeSelectOption key={f.value} value={f.value}>
              {f.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </SettingsRow>
    </SettingsGroup>
  );
}

function QuietHoursGroup({ prefs, onChange }: { prefs: Prefs; onChange: (b: Put) => void }) {
  const q = prefs.quiet_hours;
  const [draft, setDraft] = useState(q ?? { start: "22:00", end: "08:00", time_zone: LOCAL_ZONE });
  useEffect(() => {
    if (q) setDraft(q);
  }, [q]);
  const commit = (next: typeof draft) => {
    setDraft(next);
    if (q) onChange({ quiet_hours: next });
  };
  return (
    <SettingsGroup title="Quiet hours">
      <SettingsRow
        label={
          <Labelled
            about="quiet hours"
            tip="Every day in this window, alerts and the digest wait. The inbox still fills."
          >
            Every day
          </Labelled>
        }
      >
        <Switch
          aria-label="Quiet hours"
          checked={q !== null}
          onCheckedChange={(v) => onChange({ quiet_hours: v ? draft : null })}
        />
      </SettingsRow>
      {q ? (
        <>
          <SettingsRow label="From" htmlFor="quiet-start">
            <Input
              id="quiet-start"
              type="time"
              className="w-32"
              value={draft.start}
              onChange={(e) => setDraft({ ...draft, start: e.target.value })}
              onBlur={() => draft.start !== q.start && commit(draft)}
            />
          </SettingsRow>
          <SettingsRow label="To" htmlFor="quiet-end">
            <Input
              id="quiet-end"
              type="time"
              className="w-32"
              value={draft.end}
              onChange={(e) => setDraft({ ...draft, end: e.target.value })}
              onBlur={() => draft.end !== q.end && commit(draft)}
            />
          </SettingsRow>
          <SettingsRow label="Time zone" htmlFor="quiet-zone">
            <NativeSelect
              id="quiet-zone"
              value={draft.time_zone}
              onChange={(e) => commit({ ...draft, time_zone: e.target.value })}
            >
              {(ZONES.includes(draft.time_zone) ? ZONES : [draft.time_zone, ...ZONES]).map((z) => (
                <NativeSelectOption key={z} value={z}>
                  {z}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </SettingsRow>
        </>
      ) : null}
    </SettingsGroup>
  );
}

function BriefsGroup({ api, company }: { api: Api; company: CompanyAccess }) {
  const agents = useFeature("agents", company.id);
  if (!agents.enabled) return null;
  return (
    <SettingsGroup title="Briefs">
      <BriefSettingsRow api={api} co={company.id} target={{ scope: "person" }} />
    </SettingsGroup>
  );
}

const REPLY_LENGTHS = [
  { value: "short", label: "Short" },
  { value: "normal", label: "Normal" },
  { value: "detailed", label: "Detailed" },
] as const;
type ReplyLength = (typeof REPLY_LENGTHS)[number]["value"];

/**
 * DE-REPLY-STYLE: how long DEs' chat replies to the caller are (short by default). A DE also
 * sets it when the person tells it in chat ("keep it brief").
 */
export function ReplyStyleGroup({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const agents = useFeature("agents", co);
  const queryClient = useQueryClient();
  const style = useQuery({
    queryKey: notificationKeys.replyStyle(co),
    queryFn: () => api.client.replyStyle.get({ params: { co } }),
    enabled: agents.enabled,
  });
  const save = useMutation({
    mutationFn: (body: { length?: ReplyLength; quick_replies?: boolean; show_costs?: boolean }) =>
      api.client.replyStyle.set({ params: { co }, body }),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationKeys.replyStyle(co), data);
      toast.add({ title: "Saved" });
    },
    onError: (e) =>
      toast.add({ title: "Not saved", description: e instanceof Error ? e.message : String(e) }),
  });
  if (!agents.enabled) return null;
  const pending = save.isPending ? save.variables : undefined;
  const value = pending?.length ?? style.data?.length;
  // DE-QUICK-ACK: on unless the person turned it off.
  const quick = pending?.quick_replies ?? style.data?.quick_replies ?? true;
  const showCosts = pending?.show_costs ?? style.data?.show_costs ?? true;
  return (
    <SettingsGroup title="DE replies" data-slot="reply-style">
      <SettingsRow
        label={
          <Labelled
            about="reply length"
            tip="How long your DEs' chat replies are. They lead with the outcome either way; details and proof stay on the task. You can also tell a DE in chat, like “keep it brief”."
          >
            Reply length
          </Labelled>
        }
      >
        {style.isError ? (
          <span className="text-sm text-muted-foreground">Couldn't load</span>
        ) : (
          <ToggleGroup
            variant="chip"
            value={value ? [value] : []}
            // One save at a time: the next choice compares with what was saved.
            disabled={style.isPending || save.isPending}
            onValueChange={(v: unknown[]) => {
              const next = v[0] as ReplyLength | undefined;
              if (next && next !== style.data?.length) save.mutate({ length: next });
            }}
            aria-label="Reply length"
          >
            {REPLY_LENGTHS.map((l) => (
              <ToggleGroupItem key={l.value} value={l.value}>
                {l.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </SettingsRow>
      <SettingsRow
        label={
          <Labelled
            about="quick first reply"
            tip="When you message a DE, it answers at once with what it's about to do (“On it — pulling this week's numbers”), or asks a quick question first, so you can add context while it works."
          >
            Quick first reply
          </Labelled>
        }
      >
        <Switch
          aria-label="Quick first reply"
          checked={quick}
          disabled={style.isPending || style.isError || save.isPending}
          onCheckedChange={(on) => {
            if (on !== style.data?.quick_replies) save.mutate({ quick_replies: on });
          }}
        />
      </SettingsRow>
      <SettingsRow
        label={
          <Labelled
            about="agent costs in chat"
            tip="See what each agent task costs. Turn off to hide the cost labels."
          >
            Show agent costs in chat
          </Labelled>
        }
      >
        <Switch
          aria-label="Show agent costs in chat"
          checked={showCosts}
          disabled={style.isPending || style.isError || save.isPending}
          onCheckedChange={(on) => {
            if (on !== (style.data?.show_costs ?? true)) save.mutate({ show_costs: on });
          }}
        />
      </SettingsRow>
    </SettingsGroup>
  );
}

function DesktopGroup({ api, co }: { api: Api; co: string }) {
  const [permission, setPermission] = useState<Permission>(permissionNow);
  const label: Record<Permission, string> = {
    granted: "Allowed",
    denied: "Blocked in the browser's settings",
    default: "Not allowed yet",
    unsupported: "Not supported here",
  };
  return (
    <SettingsGroup title="Desktop notifications">
      <SettingsRow label="This browser" hint={label[permission]}>
        {permission === "default" ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              void window.Notification.requestPermission().then((p) =>
                setPermission(p as Permission),
              )
            }
          >
            Allow notifications
          </Button>
        ) : (
          <span className="text-[13px] text-muted-foreground">
            {permission === "granted" ? "On" : permission === "denied" ? "Blocked" : "—"}
          </span>
        )}
      </SettingsRow>
      {/* HARDEN-pwa: Web Push, so items arrive with CompanyOS closed. */}
      <PushToggle api={api} co={co} />
    </SettingsGroup>
  );
}

function ChannelsGroup({ api, company }: { api: Api; company: CompanyAccess }) {
  const chat = useFeature("chat", company.id);
  const co = company.id;
  const queryClient = useQueryClient();
  const channels = useQuery({ ...channelsQuery(api, co), enabled: chat.enabled });
  const set = useMutation({
    mutationFn: (v: {
      id: string;
      body: { notify?: "all" | "mentions" | "none"; muted?: boolean };
    }) => api.client.channels.setPrefs({ params: { co, id: v.id }, body: v.body }),
    onSuccess: () => toast.add({ title: "Saved" }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.channels(co) });
      void queryClient.invalidateQueries({ queryKey: chatKeys.prefs(co) });
    },
  });
  if (!chat.enabled) return null;
  const joined = (channels.data?.data ?? []).filter(
    (c) => c.joined && !c.archived_at && c.kind !== "dm" && c.kind !== "group_dm",
  );
  return (
    <SettingsGroup title="Channels" count={joined.length}>
      {channels.isPending ? (
        <Loading label="Loading your channels" rows={3} />
      ) : joined.length === 0 ? (
        <Notice>You have not joined a channel yet.</Notice>
      ) : (
        joined.map((c) => (
          <SettingsRow
            key={c.id}
            label={`#${c.name}`}
            hint={c.muted ? "Muted: mentions only" : undefined}
            data-channel={c.id}
          >
            <div className="flex items-center gap-3">
              <NativeSelect
                size="sm"
                aria-label={`#${c.name}: notify me about`}
                value={c.notify_level ?? "mentions"}
                onChange={(e) =>
                  set.mutate({
                    id: c.id,
                    body: { notify: e.target.value as "all" | "mentions" | "none" },
                  })
                }
              >
                {LEVELS.map((l) => (
                  <NativeSelectOption key={l.value} value={l.value}>
                    {l.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <span className="flex items-center gap-2 text-[13px] text-muted-foreground">
                <Switch
                  size="sm"
                  aria-label={`Mute #${c.name}`}
                  checked={c.muted}
                  onCheckedChange={(v) => set.mutate({ id: c.id, body: { muted: Boolean(v) } })}
                />
                <span aria-hidden="true">Mute</span>
              </span>
            </div>
          </SettingsRow>
        ))
      )}
    </SettingsGroup>
  );
}
